import { useTranslation } from "react-i18next";
import { useFetch } from "../../../../hooks/api";
import FormGroupTile from "../../basic/FormGroupTile";
import { ALGORITHM_OPTIONS } from "./constants";
import { Button, buttonRecipe } from "../../basic/Button";
import { useNavigate } from "@tanstack/react-router";
import { useAppSelector } from "../../../../redux/hooks";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { css } from "../../../../../styled-system/css";
import { Flex } from "../../../../../styled-system/jsx";

const subtitle = css({
  fontWeight: 560,
  fontSize: "1.6rem",
  color: "#73706E",
});

const explanationText = css.raw({
  margin: "small 0",
  color: "#6C757D",
  fontSize: "1.3rem",
  fontWeight: 500,
  maxWidth: "90%",
});

const reviewExplanationText = css.raw({
  maxWidth: "100%",
});

const AdPlanBiddingRules = ({
  state,
  onChange,
  review,
  skipToRulesForm,
  fieldsToValidate,
  errors = {},
  hideDescription = false,
  readOnly,
}) => {
  const { t } = useTranslation(["advertising"]);
  const navigate = useNavigate();
  const channel = useAppSelector((state) => state.channel);
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");

  const { data: biddingProgramData } = useFetch(
    ["programs", "bidding"],
    "/programs",
    { engine: "bidding", all_user_orgs: hasMultiOrgRulesModuleEnabled },
    {
      select: (data) => {
        return data.data?.results;
      },
    },
  );

  if (channel.currentChannel === "walmart") {
    return null;
  }

  const bidProgramOptions =
    biddingProgramData?.map((program) => {
      return {
        value: program.id,
        label:
          hasMultiOrgRulesModuleEnabled ?
            t(($) => $.advertising["{name} (Source: {org})"], { name: program.name, org: program.org.name })
          : program.name,
      };
    }) ?? [];

  if (state.biddingProgram && typeof state.biddingProgram === "object") {
    bidProgramOptions.unshift({
      value: state.biddingProgram?.name,
      label: state.biddingProgram?.name,
    });
  }

  return (
    <Flex wrap="wrap" style={{ margin: "3rem auto" }}>
      <div style={{ width: "100%" }}>
        <h3 className={subtitle}>
          {state.algorithm !== ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value ?
            t(($) => $.advertising["Rules Program (Optional):"])
          : t(($) => $.advertising["Rules Program:"])}
        </h3>
      </div>
      <div
        className={css({
          padding: "0 small small 0",
          width: "100%",
          lg: { width: hideDescription ? "100%" : "41.67%" },
        })}
        style={{
          paddingRight: hideDescription ? 0 : "32px",
          paddingTop: "8px",
        }}
      >
        <FormGroupTile
          type="select"
          stateName={"biddingProgram"}
          handleChildFormElement={(key, value) => {
            if (value) {
              onChange({
                [key]: value.value,
                createNewProgram: false,
              });
            } else {
              onChange({
                [key]: null,
                createNewProgram: false,
              });
            }
          }}
          isClearable={true}
          disabled={readOnly}
          defaultValue={bidProgramOptions.find((a) =>
            state.biddingProgram && typeof state.biddingProgram === "object" ?
              a.value === state.biddingProgram.name
            : a.value === state.biddingProgram,
          )}
          options={bidProgramOptions}
        />
        {state.id ?
          <div className="d-flex pt-5 align-items-center">
            {state.biddingProgram && !readOnly && (
              <>
                <div>
                  <Button
                    onPress={() =>
                      navigate({
                        to: "/user/advertising/programs/edit/$id",
                        params: { id: String(state.biddingProgram) },
                        search: { engine: "bidding" },
                      })
                    }
                  >
                    {t(($) => $.advertising["Edit Program"])}
                  </Button>
                </div>
                <p className="px-5 my-auto">{t(($) => $.advertising["Or"])}</p>
              </>
            )}

            {!readOnly && (
              <div>
                <a
                  href="/user/advertising/programs/create?engine=bidding"
                  className={buttonRecipe()}
                  disabled={state.servingStatus === "ENDED"}
                >
                  {t(($) => $.advertising["Create New Program"])}
                </a>
              </div>
            )}
          </div>
        : <>
            <p className="py-3 d-block">{t(($) => $.advertising["Or"])}</p>
            <div>
              <Button
                onPress={() => {
                  navigate({ to: ".", search: (prev) => ({ ...prev, engine: "bidding" }) });
                  skipToRulesForm();
                  onChange({ createNewProgram: true, biddingProgram: null });
                }}
              >
                {t(($) => $.advertising["Create New Rules"])}
              </Button>
            </div>
          </>
        }
      </div>
      {!hideDescription && (
        <div className={css({ width: "100%", lg: { width: "58.33%" } })}>
          <p className={css(explanationText, review && reviewExplanationText)}>
            {t(
              ($) =>
                $.advertising[
                  "Choose from predefined templates, rules programs you've previously defined, or select 'Custom' to create a new rules program tailored to this media plan."
                ],
            )}
          </p>{" "}
        </div>
      )}
      <div>
        {(
          (errors.existingBiddingProgram && fieldsToValidate?.includes("existingBiddingProgram")) ||
          (state.algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value && !state.biddingProgram)
        ) ?
          <small className="text-danger d-block pt-3">{errors.existingBiddingProgram}</small>
        : null}
      </div>
    </Flex>
  );
};

export default AdPlanBiddingRules;
